<template>
	<DataTable
		:value="mappingColumns"
		:loading="loading"
		class="p-datatable-sm p-datatable-documents"
		:autoLayout="true"
		columnResizeMode="fit"
		:rowHover="true"
		:paginator="true"
		:rows="tableListPagination.pageSize"
		paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
		:rowsPerPageOptions="[25,50,100]"
		:currentPageReportTemplate="'{first} ' + $t('Squeeze.General.To') + ' {last} ' + $t('Squeeze.General.Of') + ' {totalRecords}  '"
		:scrollable="true"
		scrollHeight="flex"
		:removableSort="true"
		filterDisplay="row"
		@page="onChangePage"
		v-model:filters="filters"
		@filter="onFilter($event)"
		sortMode="multiple"
	>
		<template #loading>
			{{  $t('Squeeze.General.Loading') }}
		</template>
		<template #empty>
			{{  $t('Squeeze.General.NoEntriesFound', { entryName: $t('Squeeze.XmlMapper.PositionData') }) }}
		</template>

		<Column v-if="false" field="id" header="ID" :sortable="false" filterMatchMode="contains" style="min-width: 4rem; max-width: 6rem;" />
		<Column field="company"  filterField="company" :header="$t('Squeeze.XmlMapper.Company')" :sorted="true" :sortable="true" :showFilterMenu="false" style="min-width: 4rem;">
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 4rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="creditor" :header="$t('Squeeze.XmlMapper.Creditor')" :sortable="true" :showFilterMenu="false"  filterMatchMode="contains" style="min-width: 7rem">
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 4rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="documentclassid" :header="$t('Squeeze.DocumentClasses.DocumentClass')" :sortable="true" :showClearButton="false" :showFilterMenu="false" filterMatchMode="contains" style="min-width: 7rem">
			<template #body="slotProp">
				<div class="column-cell" v-tooltip.bottom="getDescription(slotProp.data)">
					{{ getDescription(slotProp.data) }}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<Dropdown
					v-model="filterModel.value"
					@change="filterCallback()"
					:options="documentClasses"
					class="p-column-filter filter-field"
					optionValue="id"
					:optionLabel="getTranslatedDescription"
					:showClear="true"
					:filter="true"
					style="min-width: 9rem;"
				/>
			</template>
		</Column>
		<Column field="columnName" filter-field="columnName" :header="$t('Squeeze.XmlMapper.ColumnName')" :sortable="true" :showFilterMenu="false" style="min-width: 12rem;">
			<template #body="slotProp">
				<div class="column-cell" v-tooltip.bottom="getColumnDescriptionTooltip(slotProp.data)">
					{{ getColumnDescription(slotProp.data) }}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<Dropdown
					v-model="filterModel.value"
					@change="filterCallback()"
					:options="allColumnsOfTableLineItems"
					class="p-column-filter filter-field"
					optionValue="name"
					:optionLabel="getColumnDescriptionDropDown"
					:showClear="true"
					:filter="true"
					style="min-width: 9rem;"
				/>
			</template>
		</Column>
		<!--<Column field="namespace" :header="$t('Squeeze.XmlMapper.Namespace')" :sortable="false" filterMatchMode="contains" style="min-width: 4rem; max-width: 6rem;" />-->
		<Column field="xpath" :header="$t('Squeeze.XmlMapper.XPath')" :sortable="true" :showFilterMenu="false" filterMatchMode="contains" style="min-width: 10rem;">
			<template #body="slotProp">
				<div v-tooltip.bottom="slotProp.data.xpath">
					<template v-for="(xPath, index) in createPrintableXpath(slotProp.data.xpath)" :key="index">
						<template v-for="indexIndent in xPath.indentation" :key="indexIndent">
							&nbsp;
							&nbsp;
							&nbsp;
						</template>
						{{xPath.xPathPart}}
						<template v-if="index === createPrintableXpath(slotProp.data.xpath).length -1">
							<Button
								icon="mdi mdi-content-copy"
								type="button"
								v-tooltip.top="$t('Squeeze.General.CopyToClipboard')"
								class="p-button p-button-lg p-button-rounded p-button-text buttonCopy"
								style="height: 1.0rem;"
								@click="copyToClipboard($t, $toast, slotProp.data.xpath)"
							/>
						</template>
						<br/>
					</template>
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 4rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="active" :header="$t('Squeeze.XmlMapper.Active')" headerClass="header-center" style="min-width: 6rem; max-width: 6rem; text-align: center;">
			<template #body="slotProps">
				<Checkbox @change="onChangeActiveStatus(slotProps.data)" v-model="slotProps.data.active" :binary="true" />
			</template>
		</Column>
		<!-- Delete & Edit -->
		<Column style="min-width: 7rem; max-width: 7rem; text-align: right;">
			<template #body="slotProps">
				<Button v-if="!slotProps.data.readonly" v-tooltip.bottom="$t('Squeeze.Locators.Edit')" icon="mdi mdi-pencil-outline" class="p-button-lg p-button-rounded p-button-success p-button-text" @click="openEditDialog(slotProps.data)" />
				<Button v-if="!slotProps.data.readonly" v-tooltip.bottom="$t('Squeeze.Validation.Buttons.Delete')" icon="mdi mdi-delete-outline" class="p-button-lg p-button-rounded p-button-danger p-button-text" @click="openDeleteDialog(slotProps.data)" />
			</template>
		</Column>

		<template #paginatorstart>
			<Button :label="$t('Squeeze.General.NewEntry')" type="button" icon="mdi mdi-plus" class="p-button p-mr-2" @click="onNewEntryClick" />
			<Button v-if="showGlobalEntries" :label="$t('Squeeze.XmlMapper.HideGlobalMapping')" type="button" icon="mdi mdi-minus" class="p-button p-mr-2" @click="onClickShowGlobal(false)" />
			<Button v-if="!showGlobalEntries" :label="$t('Squeeze.XmlMapper.ShowGlobalMapping')" type="button" icon="mdi mdi-plus" class="p-button p-mr-2" @click="onClickShowGlobal(true)" />
		</template>
		<template #paginatorend></template>
	</DataTable>
</template>

<script lang="ts">
import {getTranslatedDescription} from "@/util/TranslationHelper";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Checkbox from "primevue/checkbox";
import {defineComponent, onMounted, PropType, reactive, ref} from "vue";
import {DocumentTableColumn, XmlColumnFieldTraining} from "@dex/squeeze-client-ts";
import {DocumentClass} from "@dex/squeeze-client-ts";
import Tooltip from "primevue/tooltip";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import useSqueezeStore from "@/apps/squeeze/store";
import {ClientManager} from "@/singletons/ClientManager";
import {useRoute} from "vue-router";
import {TableListSettings, TableSettings} from "@/util/TableSettings";
import {DocumentFilterObject} from "@/apps/squeeze/interfaces/DocumentSearch";
import {FilterMatchMode, FilterService} from "primevue/api";
import InputText from "primevue/inputtext";
import {showDropdownOverlay} from "@/util/StylesHelper";
import Dropdown from "primevue/dropdown";
import {copyToClipboard, downloadData} from "@/util/Export";

export default defineComponent({
	name: "XmlMapperPositionList",
	methods: {copyToClipboard, downloadData},
	components: {
		DataTable, Column, Checkbox, InputText, Dropdown,
	},
	props: {
		mappingColumns: {
			type: Array as PropType<XmlColumnFieldTraining[]>,
			default: () => [],
		},
		allColumnsOfTableLineItems: {
			type: Array as PropType<DocumentTableColumn[]>,
			default: () => [],
		},
		loading: {
			type: Boolean,
			default: false,
		},
		documentClasses: {
			type: Array as PropType<DocumentClass[]>,
			default: () => [],
		},
	},
	directives: {
		'tooltip': Tooltip,
	},
	emits: [
		"onClickDelete", "onEntrySelect", "openTraining", "openTesting", "changeShowGlobalMapping", "changeActiveStatus",
	],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();
		const route = useRoute();

		/** Vuex Store */
		const store = useSqueezeStore();

		/** User API endpoint */
		const userApi = ClientManager.getInstance().squeeze.user;

		/** Current table list pagination */
		const tableListPagination = reactive<TableListSettings>({});

		/** Should the clear filter button be shown in the table? */
		const showBtnClearFilter = ref<boolean>(false);

		/** Filters of list (Currently not activated) */
		const filters = ref<DocumentFilterObject>({});

		/** Sets the default sorting for the table (currently deactivated) */
		const multiSortMeta = ref<any[]>([{field: "company", order: -1}, {field: "creditor", order: -1}, {field: "documentclassid", order: -1}, {field: "readonly", order: 1}]);

		/** Should the button for hiding global Mappings be shown? */
		const showGlobalEntries = ref<boolean>(true);

		/**
		 * Custom Filter for Document-Class. Needed because of different types in table and documentclass-data
		 */
		FilterService.register('customWildCardFilter', (rowValue, selectedValue) => {
			if (String(rowValue) === "*") {
				return true;
			}

			if (!selectedValue) {
				return true;
			}
			return rowValue.includes(selectedValue);
		});


		/**
		 * Custom Filter for Document-Class. Needed because of different types in table and documentclass-data
		 */
		FilterService.register('customFilterDocumentClass', (rowValue, selectedValue) => {
			if (String(rowValue) === "*") {
				return true;
			}

			if (!selectedValue) {
				return true;
			}

			if (String(rowValue) === String(selectedValue)) {
				return true;
			}

			return false;
		});


		/** Init filters */
		const initFilters = () => {
			filters.value = {
				"company": {value: null, matchMode: "customWildCardFilter"},
				"creditor": {value: null, matchMode: "customWildCardFilter"},
				"documentclassid": {value: null, matchMode: "customFilterDocumentClass"},
				"columnName": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"xpath": {value: null, matchMode: FilterMatchMode.CONTAINS},
			};
		}

		/**
		 * Opens the Delete-Dialog
		 * @param row The row to delete
		 */
		const openDeleteDialog = (row: XmlColumnFieldTraining) => {
			emit("onClickDelete", row);
		}

		/**
		 * Opens the Edit-Dialog
		 * @param {BatchClass} data Data to edit
		 */
		const openEditDialog = (data: XmlColumnFieldTraining) => {
			/**
			 * The model for XmlColumnFieldTraining expects a string for the document class ID,
			 * but the document class ID is normally a number
			 */
			if (data.documentclassid === "*") {
				data.documentclassid = 0 as any;
			} else {
				data.documentclassid = Number(data.documentclassid) as any;
			}
			emit("onEntrySelect", data, true)
		}

		/** Triggered when the New Entry Button is clicked */
		const onNewEntryClick = () => {
			emit("onEntrySelect", {columnName: '', documentclassid: 0, creditor: '', readonly: false, xpath: '/', company: '', id: undefined})
		}

		/**
		 * Get the description of a column
		 * @param row
		 */
		const getColumnDescription = (row: XmlColumnFieldTraining) => {
			if (props.allColumnsOfTableLineItems) {
				const columns = props.allColumnsOfTableLineItems.filter(column => column.name === row.columnName);

				if (columns.length > 0) {
					return row.columnName + " (" + getTranslatedDescription(columns[0]) + ")";
				}
			}

			return row.columnName;
		}

		/**
		 * Get the description of a column
		 * @param row
		 */
		const getColumnDescriptionTooltip = (row: XmlColumnFieldTraining) => {
			let columnDescriptions = row.columnName;
			if (props.allColumnsOfTableLineItems) {
				const columns = props.allColumnsOfTableLineItems.filter(column => column.name === row.columnName);
				columnDescriptions = "";
				columns.forEach(column => {
					columnDescriptions += getTranslatedDescription(column) + "\n";
				})
			}

			return columnDescriptions;
		}

		/**
		 * Get the description of a column (for the dropdown)
		 * @param row
		 */
		const getColumnDescriptionDropDown = (row: any) => {
			if (props.allColumnsOfTableLineItems) {
				const column = props.allColumnsOfTableLineItems.find(column => column.name === row.name);

				if (column) {
					return row.name + " (" + getTranslatedDescription(column) + ")";
				}
			}

			return row.name;
		}

		/** Get the description of a documentClass instead of number */
		const getDescription = (row: XmlColumnFieldTraining) => {
			if (String(row.documentclassid) === '0') {
				return  "*";
			}

			if (props.documentClasses) {
				const docClass = props.documentClasses.find(docClass => String(docClass.id) === String(row.documentclassid) ||
					(docClass.id === 0 && String(row.documentclassid) === '*'));
				if (docClass) {
					return getTranslatedDescription(docClass)
				}
			}

			return row.documentclassid
		}

		onMounted(() => {
			tableListPagination.pageSize = TableSettings.getTableListPagination(store, route.name);
		});

		/** Triggered when page is changed */
		const onChangePage = (event: { page: number; rows: number }) => {
			tableListPagination.pageSize = event.rows;
			TableSettings.saveTableListPagination(t, toast, store, userApi, route.name, event.rows);
		}

		/**
		 * Triggered when the show global Button is clicked
		 * @param showGlobalMapping
		 */
		const onClickShowGlobal = (showGlobalMapping: boolean) => {
			showGlobalEntries.value = !showGlobalEntries.value;
			emit("changeShowGlobalMapping", showGlobalMapping);
		}

		initFilters();

		/**
		 * Triggered when a filter has been entered
		 * @param event
		 */
		const onFilter = (event: any) => {
			if (props.mappingColumns) {
				showBtnClearFilter.value = event.filteredValue.length < props.mappingColumns.length;
			}
		}

		/**
		 * Tries to create an printable array from an x-path string
		 * @param xPath
		 */
		const createPrintableXpath = (xPath: string) => {
			let indentation = 0;
			const parts = xPath.split('/').filter(Boolean);

			const xPaths = [] as any;

			parts.forEach((part, index) => {
				const xPathFormat = {
					indentation: 0,
					xPathPart: '',
				}

				// Most entries in positions don't have a slash at the beginning
				if (index === 0) {
					if (xPath[0]=== "/") {
						xPathFormat.xPathPart = "/" + part;
					} else {
						xPathFormat.xPathPart = part;
					}
				} else {
					xPathFormat.xPathPart = "/" + part;
				}

				xPathFormat.indentation = indentation;

				// Increase indentation for nested elements
				indentation += 1;
				xPaths.push(xPathFormat);
			});

			return xPaths;
		}

		/**
		 * Triggered when the active status of a xml training is clicked
		 * @param {XmlColumnFieldTraining} data Data to edit
		 */
		const onChangeActiveStatus = (data: XmlColumnFieldTraining) => {
			emit("changeActiveStatus", data);
		}

		return {
			getTranslatedDescription,
			store, tableListPagination, filters, multiSortMeta, showGlobalEntries,
			openDeleteDialog, onNewEntryClick, getDescription, onChangePage, openEditDialog,
			getColumnDescription, onFilter, initFilters, showDropdownOverlay,
			getColumnDescriptionDropDown, onClickShowGlobal, createPrintableXpath, getColumnDescriptionTooltip,
			onChangeActiveStatus,
		}
	},
});
</script>

<style lang="scss" scoped>

button.p-button.p-component.p-button-icon-only.p-button.p-button-lg.p-button-rounded.p-button-text.buttonCopy {
	padding: 0px;
}

::v-deep(.p-button.p-button-lg .p-button-icon) {
	font-size: 1rem;
}

// Table header
.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead tr th) {
	background-color: var(--dex-primary-light-color);
	padding: 0.6rem 0.6rem 0.2rem 0.3rem;
	border-bottom-color: var(--dex-primary-light-color);
	color: var(--dex-text-color);
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead tr th:first-child) {
	padding: 0.6rem 0.6rem 0.2rem 0.6rem;
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead .p-sortable-column-icon) {
	color: var(--dex-text-color);

	&:hover {
		color: var(--dex-text-color);
	}
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead .p-inputtext.filter-field) {
	padding: 0.4rem;
	background-color: transparent;
	color: var(--dex-text-color);
	border-radius: 0;
	border-color: var(--filter-field-color);
	border-style: solid;
	border-width: 0.063rem;

	&:focus {
		border-color: rgba(255, 255, 255, 0.8);
	}
}

.p-datatable ::v-deep(.p-datatable-thead .p-dropdown.filter-field) {
	padding:0.4rem;
	background-color: transparent;
	color: var(--dex-text-color);
	border-radius: 0;
	border-color: var(--filter-field-color);

	&:focus {
		border-color: rgba(255, 255, 255, 0.8);
	}

	.p-dropdown-label {
		padding: 0 1rem 0 0.4rem;
		color: var(--filter-field-color);
	}
}

::v-deep(td div.column-cell) {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

::v-deep(.header-center div.p-column-header-content) {
	justify-content: center;
}

</style>
